import * as pdfjsLib from 'pdfjs-dist'
import JSZip from 'jszip'

// Configure PDF.js worker - use the worker from the installed package
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).href

export class Pdf2ImgApp {
  constructor() {
    this.container = null;
    this.selectedFile = null;
    this.pdfDocument = null;
    this.settings = this.getDefaultSettings();
  }

  getDefaultSettings() {
    return {
      // Main settings
      format: 'png', // 'png', 'jpg', 'webp'
      pageRange: 'all', // 'all' or custom range like "1-5, 10"
      customRange: '',
      scale: 2, // DPI multiplier (1=72dpi, 2=144dpi, 3=216dpi, 4=288dpi)
      quality: 0.92, // For JPG/WebP (0.0 - 1.0)
      zipDownload: true, // Download as ZIP or individual files

      // Naming pattern
      namingPattern: '{original}-page-{number}', // {original}, {number}, {number:3} for padding

      // Advanced settings
      backgroundColor: '#ffffff', // For transparent PDFs
      colorMode: 'color', // 'color', 'grayscale'
      preserveAspectRatio: true,
      customWidth: null,
      customHeight: null,
    };
  }

  init(container) {
    this.container = container;
    this.render();
    this.setupEventListeners();
  }

  render() {
    this.container.innerHTML = `
      <div class="container">
        <div class="pdf2img-app">
          <!-- File Upload Section -->
          <div class="upload-section">
            <div class="upload-area" id="upload-area">
              <div class="upload-icon">📄</div>
              <h3>Select PDF File</h3>
              <p>Choose a PDF file to convert to images</p>
              <button class="upload-button" id="file-select-btn">
                <span>📎 Choose PDF File</span>
              </button>
              <input type="file" id="file-input" accept=".pdf,application/pdf" style="display: none;">
              <div class="upload-hint">Or drag and drop a PDF file here</div>
            </div>
          </div>

          <!-- PDF Info Section -->
          <div class="pdf-info-section" id="pdf-info-section" style="display: none;">
            <div class="section-header">
              <h3>📄 PDF Information</h3>
            </div>
            <div class="pdf-info-content">
              <div class="info-item">
                <span class="info-label">File:</span>
                <span class="info-value" id="pdf-filename"></span>
              </div>
              <div class="info-item">
                <span class="info-label">Pages:</span>
                <span class="info-value" id="pdf-pages"></span>
              </div>
              <div class="info-item">
                <span class="info-label">Size:</span>
                <span class="info-value" id="pdf-size"></span>
              </div>
            </div>
            <button class="action-button" id="change-file">📎 Change File</button>
          </div>

          <!-- Preview Section -->
          <div class="preview-section" id="preview-section" style="display: none;">
            <h3>Preview (First Page)</h3>
            <div class="preview-container">
              <canvas id="preview-canvas"></canvas>
              <div class="preview-loading" id="preview-loading">Loading preview...</div>
            </div>
          </div>

          <!-- Settings Section -->
          <div class="settings-section" id="settings-section" style="display: none;">
            <h3>Conversion Settings</h3>

            <!-- Main Settings -->
            <div class="settings-group main-settings">
              <div class="setting-row">
                <label for="format">🖼️ Image Format:</label>
                <select id="format" class="setting-input">
                  <option value="png">PNG (Lossless, larger files)</option>
                  <option value="jpg">JPG (Lossy, smaller files)</option>
                  <option value="webp">WebP (Modern, best compression)</option>
                </select>
              </div>

              <div class="setting-row">
                <label for="page-range-mode">📄 Pages to Convert:</label>
                <select id="page-range-mode" class="setting-input">
                  <option value="all">All pages</option>
                  <option value="custom">Custom range</option>
                </select>
              </div>

              <div class="setting-row" id="custom-range-row" style="display: none;">
                <label for="custom-range">Page Range:</label>
                <input type="text" id="custom-range" class="setting-input"
                       placeholder="e.g., 1-5, 8, 10-15">
                <div class="setting-hint" id="range-preview"></div>
              </div>

              <div class="setting-row">
                <label for="scale">📏 Resolution (DPI):</label>
                <select id="scale" class="setting-input">
                  <option value="1">72 DPI (Screen quality)</option>
                  <option value="2" selected>144 DPI (Standard quality)</option>
                  <option value="3">216 DPI (High quality)</option>
                  <option value="4">288 DPI (Print quality)</option>
                </select>
              </div>

              <div class="setting-row" id="quality-row" style="display: none;">
                <label for="quality">✨ Image Quality:</label>
                <div class="quality-control">
                  <input type="range" id="quality" class="quality-slider"
                         min="0.1" max="1.0" step="0.01" value="0.92">
                  <span class="quality-value" id="quality-value">92%</span>
                </div>
              </div>

              <div class="setting-row">
                <label for="naming-pattern">🏷️ Filename Pattern:</label>
                <select id="naming-pattern" class="setting-input">
                  <option value="{original}-page-{number}">[filename]-page-1</option>
                  <option value="{original}-{number:3}">[filename]-001</option>
                  <option value="page-{number:3}">page-001</option>
                  <option value="{original}_{number}">[filename]_1</option>
                </select>
                <div class="setting-hint" id="naming-preview"></div>
              </div>

              <div class="setting-row">
                <label for="zip-download">📦 Download as ZIP:</label>
                <div class="checkbox-group">
                  <input type="checkbox" id="zip-download" class="setting-checkbox" checked>
                  <span class="checkbox-label">Download all images in a single ZIP file</span>
                </div>
              </div>
            </div>

            <!-- Advanced Settings Toggle -->
            <button class="advanced-toggle" id="advanced-toggle">
              <span>⚙️ Advanced Settings</span>
              <span class="toggle-icon">▼</span>
            </button>

            <!-- Advanced Settings (Hidden by default) -->
            <div class="settings-group advanced-settings" id="advanced-settings" style="display: none;">
              <div class="setting-row">
                <label for="color-mode">🎨 Color Mode:</label>
                <select id="color-mode" class="setting-input">
                  <option value="color">Full Color</option>
                  <option value="grayscale">Grayscale</option>
                </select>
              </div>

              <div class="setting-row">
                <label for="background-color">🎨 Background Color:</label>
                <div class="color-input-group">
                  <input type="color" id="background-color" class="color-input" value="#ffffff">
                  <span class="color-label">For transparent PDFs</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Convert Section -->
          <div class="convert-section" id="convert-section" style="display: none;">
            <button class="convert-button" id="convert-button">
              <span class="convert-icon">🔄</span>
              <span class="convert-text">Convert to Images</span>
            </button>

            <div class="progress-section" id="progress-section" style="display: none;">
              <div class="progress-bar">
                <div class="progress-fill" id="progress-fill"></div>
              </div>
              <div class="progress-text" id="progress-text">Converting...</div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  setupEventListeners() {
    const fileInput = document.getElementById('file-input');
    const fileSelectBtn = document.getElementById('file-select-btn');
    const uploadArea = document.getElementById('upload-area');
    const changeFileBtn = document.getElementById('change-file');
    const convertBtn = document.getElementById('convert-button');
    const advancedToggle = document.getElementById('advanced-toggle');

    // File selection
    fileSelectBtn.addEventListener('click', () => fileInput.click());
    changeFileBtn.addEventListener('click', () => fileInput.click());
    fileInput.addEventListener('change', (e) => this.handleFileSelection(e.target.files[0]));

    // Drag and drop
    uploadArea.addEventListener('dragover', this.handleDragOver.bind(this));
    uploadArea.addEventListener('drop', this.handleFileDrop.bind(this));
    uploadArea.addEventListener('dragleave', this.handleDragLeave.bind(this));

    // Convert button
    convertBtn.addEventListener('click', () => this.convertToImages());

    // Advanced settings toggle
    advancedToggle.addEventListener('click', () => this.toggleAdvancedSettings());

    // Settings change listeners
    this.setupSettingsListeners();
  }

  setupSettingsListeners() {
    // Format change
    document.getElementById('format').addEventListener('change', (e) => {
      this.settings.format = e.target.value;
      this.updateQualityVisibility();
      this.updateNamingPreview();
    });

    // Page range mode
    document.getElementById('page-range-mode').addEventListener('change', (e) => {
      const customRow = document.getElementById('custom-range-row');
      if (e.target.value === 'custom') {
        customRow.style.display = 'grid';
        this.settings.pageRange = 'custom';
      } else {
        customRow.style.display = 'none';
        this.settings.pageRange = 'all';
      }
      this.updateRangePreview();
    });

    // Custom range
    document.getElementById('custom-range').addEventListener('input', (e) => {
      this.settings.customRange = e.target.value;
      this.updateRangePreview();
    });

    // Scale
    document.getElementById('scale').addEventListener('change', (e) => {
      this.settings.scale = parseFloat(e.target.value);
    });

    // Quality
    document.getElementById('quality').addEventListener('input', (e) => {
      this.settings.quality = parseFloat(e.target.value);
      document.getElementById('quality-value').textContent = Math.round(e.target.value * 100) + '%';
    });

    // Naming pattern
    document.getElementById('naming-pattern').addEventListener('change', (e) => {
      this.settings.namingPattern = e.target.value;
      this.updateNamingPreview();
    });

    // ZIP download
    document.getElementById('zip-download').addEventListener('change', (e) => {
      this.settings.zipDownload = e.target.checked;
    });

    // Color mode
    document.getElementById('color-mode').addEventListener('change', (e) => {
      this.settings.colorMode = e.target.value;
    });

    // Background color
    document.getElementById('background-color').addEventListener('change', (e) => {
      this.settings.backgroundColor = e.target.value;
    });
  }

  handleDragOver(e) {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.add('drag-over');
  }

  handleDragLeave(e) {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.remove('drag-over');
  }

  handleFileDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.remove('drag-over');

    const file = e.dataTransfer.files[0];
    if (file) {
      this.handleFileSelection(file);
    }
  }

  async handleFileSelection(file) {
    if (!file) return;

    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      this.showError('Please select a valid PDF file');
      return;
    }

    this.selectedFile = file;

    try {
      // Load PDF
      const arrayBuffer = await file.arrayBuffer();
      this.pdfDocument = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

      // Update UI
      document.getElementById('pdf-filename').textContent = file.name;
      document.getElementById('pdf-pages').textContent = this.pdfDocument.numPages;
      document.getElementById('pdf-size').textContent = this.formatFileSize(file.size);

      // Show sections
      document.getElementById('pdf-info-section').style.display = 'block';
      document.getElementById('preview-section').style.display = 'block';
      document.getElementById('settings-section').style.display = 'block';
      document.getElementById('convert-section').style.display = 'block';

      // Generate preview
      await this.generatePreview();

      // Update previews
      this.updateQualityVisibility();
      this.updateNamingPreview();
      this.updateRangePreview();

    } catch (error) {
      console.error('Error loading PDF:', error);
      this.showError('Failed to load PDF: ' + error.message);
    }
  }

  async generatePreview() {
    const canvas = document.getElementById('preview-canvas');
    const loading = document.getElementById('preview-loading');

    try {
      loading.style.display = 'block';

      // Get first page
      const page = await this.pdfDocument.getPage(1);
      const viewport = page.getViewport({ scale: 1.5 });

      // Set canvas dimensions
      canvas.width = viewport.width;
      canvas.height = viewport.height;

      // Render page
      const context = canvas.getContext('2d');
      await page.render({
        canvasContext: context,
        viewport: viewport
      }).promise;

      loading.style.display = 'none';
    } catch (error) {
      console.error('Error generating preview:', error);
      loading.textContent = 'Failed to generate preview';
    }
  }

  updateQualityVisibility() {
    const qualityRow = document.getElementById('quality-row');
    const format = this.settings.format;

    // Show quality slider for JPG and WebP
    if (format === 'jpg' || format === 'webp') {
      qualityRow.style.display = 'grid';
    } else {
      qualityRow.style.display = 'none';
    }
  }

  updateNamingPreview() {
    if (!this.selectedFile) return;

    const preview = document.getElementById('naming-preview');
    const originalName = this.selectedFile.name.replace('.pdf', '');
    const format = this.settings.format;

    const sampleName = this.generateFilename(originalName, 1, format);
    preview.textContent = `Example: ${sampleName}`;
  }

  updateRangePreview() {
    if (!this.pdfDocument) return;

    const preview = document.getElementById('range-preview');

    if (this.settings.pageRange === 'all') {
      preview.textContent = `Converting all ${this.pdfDocument.numPages} pages`;
      preview.className = 'setting-hint valid';
      return;
    }

    const indices = this.parsePageRange(this.settings.customRange, this.pdfDocument.numPages);

    if (indices.length === 0) {
      preview.textContent = 'Invalid page range';
      preview.className = 'setting-hint error';
    } else {
      preview.textContent = `Converting ${indices.length} page(s): ${this.formatPageList(indices)}`;
      preview.className = 'setting-hint valid';
    }
  }

  parsePageRange(rangeString, totalPages) {
    if (!rangeString.trim()) return [];

    const indices = [];
    const ranges = rangeString.split(',').map(s => s.trim());

    for (const range of ranges) {
      if (range.includes('-')) {
        const [start, end] = range.split('-').map(s => parseInt(s.trim()));
        if (start && end && start <= totalPages && end <= totalPages && start <= end) {
          for (let i = start; i <= end; i++) {
            if (!indices.includes(i)) {
              indices.push(i);
            }
          }
        }
      } else {
        const pageNum = parseInt(range);
        if (pageNum && pageNum >= 1 && pageNum <= totalPages && !indices.includes(pageNum)) {
          indices.push(pageNum);
        }
      }
    }

    return indices.sort((a, b) => a - b);
  }

  formatPageList(indices) {
    if (indices.length <= 5) {
      return indices.join(', ');
    }
    return `${indices[0]}, ${indices[1]}, ... ${indices[indices.length - 1]}`;
  }

  generateFilename(originalName, pageNumber, format) {
    let filename = this.settings.namingPattern;

    // Replace {original}
    filename = filename.replace('{original}', originalName);

    // Replace {number} with padding
    const paddingMatch = filename.match(/\{number:(\d+)\}/);
    if (paddingMatch) {
      const padding = parseInt(paddingMatch[1]);
      const paddedNumber = pageNumber.toString().padStart(padding, '0');
      filename = filename.replace(/\{number:\d+\}/, paddedNumber);
    } else {
      filename = filename.replace('{number}', pageNumber);
    }

    return `${filename}.${format}`;
  }

  toggleAdvancedSettings() {
    const advancedSettings = document.getElementById('advanced-settings');
    const toggleIcon = document.querySelector('.toggle-icon');

    if (advancedSettings.style.display === 'none') {
      advancedSettings.style.display = 'block';
      toggleIcon.textContent = '▲';
    } else {
      advancedSettings.style.display = 'none';
      toggleIcon.textContent = '▼';
    }
  }

  async convertToImages() {
    if (!this.pdfDocument) {
      this.showError('Please select a PDF file first');
      return;
    }

    try {
      this.showProgress('Initializing conversion...');

      // Determine which pages to convert
      let pageNumbers;
      if (this.settings.pageRange === 'all') {
        pageNumbers = Array.from({ length: this.pdfDocument.numPages }, (_, i) => i + 1);
      } else {
        pageNumbers = this.parsePageRange(this.settings.customRange, this.pdfDocument.numPages);
        if (pageNumbers.length === 0) {
          throw new Error('Invalid page range');
        }
      }

      const images = [];
      const originalName = this.selectedFile.name.replace('.pdf', '');

      // Convert each page
      for (let i = 0; i < pageNumbers.length; i++) {
        const pageNum = pageNumbers[i];
        const progress = i / pageNumbers.length;

        this.updateProgress(
          `Converting page ${pageNum} of ${this.pdfDocument.numPages}...`,
          progress
        );

        const imageData = await this.convertPageToImage(pageNum);
        const filename = this.generateFilename(originalName, pageNum, this.settings.format);

        images.push({
          filename: filename,
          data: imageData
        });
      }

      this.updateProgress('Preparing download...', 0.95);

      // Download images
      if (this.settings.zipDownload && images.length > 1) {
        await this.downloadAsZip(images, originalName);
      } else {
        images.forEach(image => {
          this.downloadImage(image.data, image.filename);
        });
      }

      this.updateProgress('Complete!', 1.0);
      this.hideProgress();
      this.showSuccess(`Successfully converted ${images.length} page(s) to ${this.settings.format.toUpperCase()}!`);

    } catch (error) {
      console.error('Error converting PDF:', error);
      this.hideProgress();
      this.showError(`Conversion failed: ${error.message}`);
    }
  }

  async convertPageToImage(pageNumber) {
    // Get the page
    const page = await this.pdfDocument.getPage(pageNumber);
    const viewport = page.getViewport({ scale: this.settings.scale });

    // Create canvas
    const canvas = document.createElement('canvas');
    canvas.width = viewport.width;
    canvas.height = viewport.height;

    const context = canvas.getContext('2d');

    // Set background color
    context.fillStyle = this.settings.backgroundColor;
    context.fillRect(0, 0, canvas.width, canvas.height);

    // Render page to canvas
    await page.render({
      canvasContext: context,
      viewport: viewport
    }).promise;

    // Apply grayscale if needed
    if (this.settings.colorMode === 'grayscale') {
      this.applyGrayscale(context, canvas.width, canvas.height);
    }

    // Convert canvas to blob
    const mimeType = this.getMimeType(this.settings.format);
    const blob = await new Promise((resolve) => {
      if (this.settings.format === 'png') {
        canvas.toBlob(resolve, mimeType);
      } else {
        canvas.toBlob(resolve, mimeType, this.settings.quality);
      }
    });

    return blob;
  }

  applyGrayscale(context, width, height) {
    const imageData = context.getImageData(0, 0, width, height);
    const data = imageData.data;

    for (let i = 0; i < data.length; i += 4) {
      const gray = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
      data[i] = gray;
      data[i + 1] = gray;
      data[i + 2] = gray;
    }

    context.putImageData(imageData, 0, 0);
  }

  getMimeType(format) {
    const types = {
      'png': 'image/png',
      'jpg': 'image/jpeg',
      'webp': 'image/webp'
    };
    return types[format] || 'image/png';
  }

  async downloadAsZip(images, originalName) {
    const zip = new JSZip();

    // Add all images to ZIP
    images.forEach(image => {
      zip.file(image.filename, image.data);
    });

    // Generate ZIP file
    const zipBlob = await zip.generateAsync({ type: 'blob' });

    // Download ZIP
    const zipFilename = `${originalName}-images.zip`;
    this.downloadImage(zipBlob, zipFilename);
  }

  downloadImage(blob, filename) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  showProgress(message) {
    const progressSection = document.getElementById('progress-section');
    const convertButton = document.getElementById('convert-button');

    progressSection.style.display = 'block';
    convertButton.disabled = true;

    this.updateProgress(message, 0);
  }

  updateProgress(message, progress) {
    const progressText = document.getElementById('progress-text');
    const progressFill = document.getElementById('progress-fill');

    progressText.textContent = message;
    progressFill.style.width = `${Math.round(progress * 100)}%`;
  }

  hideProgress() {
    const progressSection = document.getElementById('progress-section');
    const convertButton = document.getElementById('convert-button');

    setTimeout(() => {
      progressSection.style.display = 'none';
      convertButton.disabled = false;
    }, 2000);
  }

  showSuccess(message) {
    this.showNotification(message, 'success');
  }

  showError(message) {
    this.showNotification(message, 'error');
  }

  showNotification(message, type) {
    const notification = document.createElement('div');
    notification.className = `notification notification-${type}`;
    notification.textContent = message;

    this.container.appendChild(notification);

    setTimeout(() => {
      if (notification.parentNode) {
        notification.parentNode.removeChild(notification);
      }
    }, 5000);
  }

  destroy() {
    this.selectedFile = null;
    this.pdfDocument = null;
    if (this.container) {
      this.container.innerHTML = '';
    }
  }
}
